import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { 
  Check, X, UserRound, Clock, Loader2, UserPlus, 
  CalendarDays, ArrowRight, ShieldCheck, Mail
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { Badge } from "./badge";
import { Button } from "./button";
import { UserProfileLink } from "./user-profile-link";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface FriendInvitesProps {
  searchQuery?: string;
}

// Extended type for friend invites that includes the expected fields
interface FriendInvite {
  id: number;
  userId: number;
  friendId: number;
  status: string;
  createdAt: string;
  username?: string; // May come from a join
  profile?: {
    avatarId?: number | null;
    avatarSource?: string;
    status?: string;
  };
}

export function FriendInvites({ searchQuery = "" }: Readonly<FriendInvitesProps>) {
  const { user } = useAuth();
  const {
    data: invites,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["friends", "invites"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/friends");
      return res.json();
    },
  });

  // Store avatar data for all invites
  const inviteAvatars = useQuery({
    queryKey: ["invite-avatars", invites],
    queryFn: async () => {
      if (!invites?.length) return {};
      
      const avatarData: Record<number, { imageUrl: string | null }> = {};
      
      for (const invite of invites) {
        if (!invite.profile?.avatarId) continue;
        

        const source = invite.profile.avatarSource || "default";
        const endpoint = source === "shop" 
          ? `/api/customization/shopItem/${invite.profile.avatarId}` 
          : `/api/customization/avatars/${invite.profile.avatarId}`;
          
        try {
          const res = await apiRequest("GET", endpoint);
          if (res.ok) {
            const data = await res.json();
            const otherUserId = invite.userId === user?.id ? invite.friendId : invite.userId;
            avatarData[otherUserId] = data;
          }
        } catch (error) {
          console.error("Error fetching avatar:", error);
        }
      }
      
      return avatarData;
    },
    enabled: !!invites?.length && !!user?.id,
  });

  const respondToRequest = useMutation({
    mutationFn: async ({ friendId, status }: { friendId: number; status: string; }) => {
      const res = await apiRequest("POST", `/api/friends/respond`, {
        requestId: friendId,
        status,
      });
      return res.json();
    },
    onSuccess: (data, variables) => {
      refetch();
      toast({
        title: variables.status === "accepted" ? "Friend request accepted" : "Friend request declined",
        description: variables.status === "accepted" 
          ? "You can now play games together and chat" 
          : "The request has been removed",
      });
    },
    onError: (error) => {
      toast({
        title: "Error processing request",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/60">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        <p>Loading requests...</p>
      </div>
    );
  }

  const filteredInvites = invites?.filter(
    (invite: FriendInvite) =>
      (invite.username?.toLowerCase().includes(searchQuery.toLowerCase()) || !searchQuery) &&
      invite.status === "pending",
  );

  if (!filteredInvites?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/60">
        <UserPlus className="w-10 h-10 opacity-30 mb-4" />
        <p className="text-lg mb-1">No pending requests</p>
        <p className="text-sm text-white/40 text-center px-4">
          Friend requests will appear here when you receive them
        </p>
      </div>
    );
  }

  return (
    <motion.div 
      className="space-y-3"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {filteredInvites.map((invite: FriendInvite) => {
        const isReceived = invite.userId !== user?.id;
        const otherUserId = isReceived ? invite.userId : invite.friendId;
        const formattedDate = new Date(invite.createdAt).toLocaleDateString(undefined, { 
          month: 'short', 
          day: 'numeric' 
        });
        
        // Use a fallback for username if it's not available in the data
        const displayName = invite.username || `User ${otherUserId}`;
        // Get the initials for the avatar fallback
        const initials = displayName.substring(0, 2).toUpperCase();
        const avatarData = inviteAvatars.data?.[otherUserId];
        
        return (
          <motion.div 
            key={invite.id} 
            variants={itemVariants}
            className={cn(
              "rounded-lg overflow-hidden border border-white/5 backdrop-blur-sm",
              "bg-gradient-to-r from-black/60 to-black/40",
              "hover:border-primary/20 transition-all duration-200"
            )}
          >
            <div className="px-4 py-3 border-b border-white/5 bg-black/30 flex justify-between items-center">
              <div className="flex items-center gap-2">
                {isReceived ? (
                  <ArrowRight className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <ArrowRight className="h-3.5 w-3.5 text-blue-400 rotate-180" />
                )}
                <span className="text-sm text-white/80">
                  {isReceived ? "Received Request" : "Sent Request"}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-white/50">
                <CalendarDays className="h-3 w-3" />
                <span>{formattedDate}</span>
              </div>
            </div>

            <div className="p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <UserProfileLink userId={otherUserId} username={displayName}>
                    <Avatar className="h-12 w-12 ring-2 ring-black/50 cursor-pointer hover:ring-primary/30 transition-all duration-300">
                      {avatarData?.imageUrl ? (
                        <AvatarImage src={avatarData.imageUrl} className="object-cover" />
                      ) : (
                        <AvatarFallback className="bg-black text-primary font-bold">
                          {initials}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  </UserProfileLink>
                  <div>
                    <UserProfileLink userId={otherUserId} username={displayName}>
                      <h3 className="text-base font-medium text-white cursor-pointer hover:text-primary transition-colors">
                        {displayName}
                      </h3>
                    </UserProfileLink>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className="bg-black/40 text-white/70 hover:bg-black/60 py-0 px-2 h-5">Pending</Badge>
                    </div>
                  </div>
                </div>

                {isReceived ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="h-8 px-3 rounded-full bg-green-600/90 hover:bg-green-500 text-white border-0"
                      onClick={() => respondToRequest.mutate({
                        friendId: invite.id,
                        status: "accepted",
                      })}
                      disabled={respondToRequest.isPending}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" />
                      <span className="text-xs">Accept</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 rounded-full bg-transparent border-white/10 hover:bg-white/10 text-white/80"
                      onClick={() => respondToRequest.mutate({
                        friendId: invite.id,
                        status: "rejected",
                      })}
                      disabled={respondToRequest.isPending}
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      <span className="text-xs">Decline</span>
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/40 italic">Awaiting response</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0 rounded-full bg-transparent border-white/10 hover:bg-red-500/10 hover:border-red-500/30 text-white/70 hover:text-red-400"
                      onClick={() => respondToRequest.mutate({
                        friendId: invite.id,
                        status: "cancelled",
                      })}
                      disabled={respondToRequest.isPending}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
